import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useCandidatePoolsApi } from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../components/GenericFormDrawer';

// ADAY HAVUZU (SPEC F2) — "Yetenek Havuzu" (TD) ile KARIŞMASIN: burası işe alımın
// sessiz aday deposudur. Rota ve menü AT.Write'tır (havuzlar tüm adayları içerir).
// Satır tıklaması havuz detayına (üyeler + davet) gider.
export default function AtsCandidatePoolsPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, row: null });
  const [deleting, setDeleting] = useState(false);

  const {
    fetching, loadFailed, data, pagination, columns: baseColumns, objectSchema, create, update, remove,
  } = useCandidatePoolsApi(queryParams);

  const openPool = useCallback((row) => {
    if (row?.id) history.push(`/ats/candidate-pools/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((next) => setFilterList(next), []);

  const handleSave = useCallback(async (values) => {
    const ok = drawer.row?.id ? await update(drawer.row.id, values) : await create(values);
    if (ok) setDrawer({ open: false, row: null });
    return ok;
  }, [drawer.row, create, update]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.row) return;
    setDeleting(true);
    await remove(deleteDialog.row.id);
    setDeleting(false);
    setDeleteDialog({ isOpen: false, row: null });
  }, [deleteDialog.row, remove]);

  const buildActions = useCallback((row) => [
    { type: 'details', label: t('ats.candidatePool.openPool'), onClick: () => openPool(row) },
    { type: 'edit', label: t('common.edit'), onClick: () => setDrawer({ open: true, row }) },
    { type: 'divider' },
    { type: 'delete', label: t('common.delete'), onClick: () => setDeleteDialog({ isOpen: true, row }) },
  ], [t, openPool]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <span onClick={(e) => e.stopPropagation()} role="presentation">
              <OptionsMenu actions={buildActions(row)} row={row} />
            </span>
          );
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  const initialValues = useMemo(() => (drawer.row
    ? { name: drawer.row.name || '', description: drawer.row.description || '' }
    : null), [drawer.row]);

  return (
    <div>
      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        title={drawer.row ? t('ats.candidatePool.edit') : t('ats.candidatePool.new')}
        schema={objectSchema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.candidatePool.deleteConfirm', { name: deleteDialog.row?.name || '' })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, row: null })}
        loading={deleting}
      />

      {loadFailed && <LoadErrorBanner message={t('ats.candidatePool.loadFailed')} />}

      <DataTable
        title={t('ats.candidatePool.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord
        newButtonLabel={t('ats.candidatePool.new')}
        onNewButtonClick={() => setDrawer({ open: true, row: null })}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        onRowClick={openPool}
      />
    </div>
  );
}
